import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { APIProvider, useMapsLibrary } from '@vis.gl/react-google-maps';

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? '';

export const HAS_GOOGLE_MAPS_KEY =
  Boolean(GOOGLE_MAPS_API_KEY) &&
  GOOGLE_MAPS_API_KEY !== 'your-google-maps-api-key' &&
  !GOOGLE_MAPS_API_KEY.includes('placeholder');

export interface SelectedPlace {
  name: string;
  address: string;
  latitude: number;
  longitude: number;
}

type PlacePrediction = google.maps.places.AutocompletePrediction;

function PlacesAutocompleteInner({
  value,
  label,
  placeholder,
  helperText,
  onChange,
  onPlaceSelected,
}: {
  value: string;
  label: string;
  placeholder: string;
  helperText?: string;
  onChange: (value: string) => void;
  onPlaceSelected: (place: SelectedPlace) => void;
}) {
  const places = useMapsLibrary('places');
  const [inputValue, setInputValue] = useState(value);
  const [options, setOptions] = useState<PlacePrediction[]>([]);
  const [loading, setLoading] = useState(false);
  const [helper, setHelper] = useState(helperText ?? 'Type at least 2 characters to search');
  const sessionTokenRef = useRef<google.maps.places.AutocompleteSessionToken | null>(null);
  const placesServiceRef = useRef<google.maps.places.PlacesService | null>(null);
  const attributionRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    if (!places) return;
    if (!sessionTokenRef.current) {
      sessionTokenRef.current = new places.AutocompleteSessionToken();
    }
    if (!placesServiceRef.current && attributionRef.current) {
      placesServiceRef.current = new places.PlacesService(attributionRef.current);
    }
  }, [places]);

  useEffect(() => {
    if (!places) return;

    const query = inputValue.trim();
    if (query.length < 2) {
      setOptions([]);
      setLoading(false);
      setHelper(helperText ?? 'Type at least 2 characters to search');
      return;
    }

    const service = new places.AutocompleteService();
    let cancelled = false;
    setLoading(true);

    const timer = window.setTimeout(() => {
      service.getPlacePredictions(
        {
          input: query,
          sessionToken: sessionTokenRef.current ?? undefined,
        },
        (predictions, status) => {
          if (cancelled) return;
          setLoading(false);

          if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
            setOptions([]);
            setHelper('No places found');
            return;
          }

          if (status !== google.maps.places.PlacesServiceStatus.OK || !predictions) {
            setOptions([]);
            setHelper(`Places search failed (${status}). Enable Places API for your key.`);
            return;
          }

          setOptions(predictions);
          setHelper('Select a suggestion to set the visit location');
        },
      );
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [places, inputValue, helperText]);

  const resolvePlace = useCallback(
    (prediction: PlacePrediction) => {
      if (!places) return;

      if (!placesServiceRef.current) {
        const el = attributionRef.current ?? document.createElement('div');
        placesServiceRef.current = new places.PlacesService(el);
      }

      placesServiceRef.current.getDetails(
        {
          placeId: prediction.place_id,
          fields: ['formatted_address', 'geometry', 'name'],
          sessionToken: sessionTokenRef.current ?? undefined,
        },
        (place, status) => {
          sessionTokenRef.current = new places.AutocompleteSessionToken();

          if (status !== google.maps.places.PlacesServiceStatus.OK || !place?.geometry?.location) {
            setHelper('Could not load place details. Try another suggestion.');
            return;
          }

          const address = place.formatted_address ?? place.name ?? prediction.description;
          const name =
            place.name ??
            prediction.structured_formatting?.main_text ??
            address.split(',')[0] ??
            address;
          const latitude = place.geometry.location.lat();
          const longitude = place.geometry.location.lng();

          setInputValue(address);
          onChange(address);
          onPlaceSelected({ name, address, latitude, longitude });
          setHelper('Location selected');
        },
      );
    },
    [places, onChange, onPlaceSelected],
  );

  if (!places) {
    return (
      <TextField
        label={label}
        fullWidth
        disabled
        value={inputValue}
        helperText="Loading Google Places…"
      />
    );
  }

  return (
    <>
      <div ref={attributionRef} style={{ display: 'none' }} />
      <Autocomplete
        freeSolo
        fullWidth
        options={options}
        filterOptions={(x) => x}
        getOptionLabel={(option) =>
          typeof option === 'string' ? option : option.description
        }
        isOptionEqualToValue={(a, b) =>
          typeof a === 'string' || typeof b === 'string'
            ? a === b
            : a.place_id === b.place_id
        }
        inputValue={inputValue}
        onInputChange={(_, next, reason) => {
          setInputValue(next);
          if (reason === 'input' || reason === 'clear') {
            onChange(next);
          }
        }}
        onChange={(_, selected) => {
          if (selected && typeof selected !== 'string') {
            resolvePlace(selected);
          }
        }}
        loading={loading}
        noOptionsText={
          inputValue.trim().length < 2 ? 'Keep typing…' : 'No matching places'
        }
        componentsProps={{
          popper: {
            sx: { zIndex: (theme) => theme.zIndex.modal + 2 },
          },
        }}
        renderOption={(props, option) => (
          <li {...props} key={option.place_id}>
            <LocationOnIcon color="action" sx={{ mr: 1.5, flexShrink: 0 }} />
            <Box>
              <Typography variant="body2" fontWeight={600}>
                {option.structured_formatting?.main_text ?? option.description}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {option.structured_formatting?.secondary_text}
              </Typography>
            </Box>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            helperText={helper}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <LocationOnIcon color="primary" fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: (
                <>
                  {loading ? <CircularProgress color="inherit" size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </>
  );
}

export function PlacesSearchField({
  value,
  onChange,
  onPlaceSelected,
  label = 'Search area / address',
  placeholder = 'e.g. Andheri East, Mumbai',
  helperText,
}: {
  value: string;
  onChange: (value: string) => void;
  onPlaceSelected: (place: SelectedPlace) => void;
  label?: string;
  placeholder?: string;
  helperText?: string;
}) {
  if (!HAS_GOOGLE_MAPS_KEY) {
    return (
      <TextField
        label={label}
        fullWidth
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        helperText="Google Maps key missing — enter an address manually, then set lat/lng elsewhere"
      />
    );
  }

  return (
    <APIProvider apiKey={GOOGLE_MAPS_API_KEY} libraries={['places']}>
      <PlacesAutocompleteInner
        value={value}
        label={label}
        placeholder={placeholder}
        helperText={helperText}
        onChange={onChange}
        onPlaceSelected={onPlaceSelected}
      />
    </APIProvider>
  );
}
